import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DatePicker, Segmented, Select, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import customerService from '@/services/customer.service';
import type { ReportParams } from '@/services/reportDocuments.service';
import ReportDocumentPanel from './ReportDocumentPanel';
import { isoDate } from './periodPresets';

const { Title, Text } = Typography;

type Party = 'customer' | 'supplier';
type View = 'summary' | 'detail';

async function loadParties(party: Party): Promise<Array<{ value: string; label: string }>> {
  if (party === 'customer') {
    const rows = (await customerService.getActiveCustomers()) as Array<{ id: string; customer_name?: string; customer_code?: string }>;
    return rows.map((c) => ({ value: c.id, label: `${c.customer_name ?? ''}${c.customer_code ? ` (${c.customer_code})` : ''}` }));
  }
  const res = await api.get<any>('/procurement/suppliers');
  const data = (res as any)?.data ?? res;
  const rows: any[] = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
  return rows.map((s) => ({ value: s.id, label: `${s.supplier_name ?? ''}${s.supplier_code ? ` (${s.supplier_code})` : ''}` }));
}

/** Historical aged receivables / payables as at a date, built on the statement engine (posted documents only). */
const PartyOutstandingReport: React.FC<{ party: Party }> = ({ party }) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const reportKey = `${party}-outstanding`;
  const [searchParams, setSearchParams] = useSearchParams();
  const [asOf, setAsOf] = useState<Dayjs>(searchParams.get('as_of') ? dayjs(searchParams.get('as_of')) : dayjs());
  const [partyId, setPartyId] = useState<string | undefined>(searchParams.get(`${party}_id`) ?? searchParams.get('party_id') ?? undefined);
  const [view, setView] = useState<View>(searchParams.get('view') === 'detail' ? 'detail' : 'summary');
  const [params, setParams] = useState<ReportParams | null>(null);

  const { data: options = [] } = useQuery({ queryKey: ['outstanding-parties', party], queryFn: () => loadParties(party) });

  const generate = (next?: Partial<{ asOf: Dayjs; partyId?: string; view: View }>) => {
    const p: ReportParams = { as_of: isoDate(next?.asOf ?? asOf), view: next?.view ?? view };
    const id = next && 'partyId' in next ? next.partyId : partyId;
    if (id) p.party_id = id;
    setParams(p);
    setSearchParams(p as Record<string, string>, { replace: true });
  };

  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filters = (
      <Space wrap size="middle" align="end">
        <Space direction="vertical" size={4}>
          <Text type="secondary">{t('asAt')}</Text>
          <DatePicker value={asOf} format="DD-MM-YYYY" allowClear={false} onChange={(d) => d && setAsOf(d)} />
        </Space>
        <Space direction="vertical" size={4}>
          <Text type="secondary">{party === 'customer' ? t('customer') : t('supplier')}</Text>
          <Select
            allowClear
            placeholder={party === 'customer' ? t('allCustomers') : t('allSuppliers')}
            value={partyId}
            onChange={setPartyId}
            options={options}
            style={{ width: 320 }}
            showSearch
            optionFilterProp="label"
          />
        </Space>
        <Segmented<View>
          value={view}
          onChange={(v) => {
            setView(v);
            if (params) generate({ view: v });
          }}
          options={[
            { value: 'summary', label: t('summary') },
            { value: 'detail', label: t('openItemsDetail') },
          ]}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={() => generate()}>
          {tc('generate')}
        </Button>
      </Space>
  );

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 4 }}>
        {party === 'customer' ? t('customerOutstanding') : t('supplierOutstanding')}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {party === 'customer' ? t('outstandingHintCustomer') : t('outstandingHintSupplier')}
      </Text>
      <ReportDocumentPanel reportKey={reportKey} params={params} filters={filters} />
    </div>
  );
};

export default PartyOutstandingReport;
